(() => {
  const root = document.querySelector('.fp-guides-index');
  if (!root) return;

  const featured = root.querySelector('[data-guide-featured]');
  const grid = root.querySelector('[data-guide-grid]');
  const list = root.querySelector('[data-guide-list]');
  const count = root.querySelector('[data-guide-count]');
  const empty = root.querySelector('[data-guide-empty]');
  const topicButtons = [...root.querySelectorAll('[data-guide-topic]')];
  const speciesButtons = [...root.querySelectorAll('[data-guide-species]')];
  const nativePagination = root.querySelector('nav[aria-label="Páginas de guías"]');
  const initialLastPage = getLastPageFrom(document);
  const pagination = nativePagination || document.createElement('nav');
  const pageSize = 50;
  const cardMap = new Map();
  let species = 'all';
  let topic = 'all';
  let page = 1;
  let loadingAllPages = false;
  let allPagesPromise = null;
  let allPagesLoaded = initialLastPage <= 1;
  const baseTitle = document.title.replace(/\s*[-–—]\s*Página\s+\d+\s*$/i, '') || document.title;

  if (!nativePagination) {
    pagination.setAttribute('aria-label', 'Páginas de guías');
    root.querySelector('.fp-guides-index__help')?.before(pagination);
  }
  pagination.classList.add('fp-guides-index__pagination');
  pagination.hidden = true;

  const normalizeSpecies = value => ['all', 'perro', 'gato'].includes(value) ? value : 'all';
  const normalizeTopic = value => ['all', 'alimento', 'salud', 'razas', 'arena', 'consejos'].includes(value) ? value : 'all';
  const matchesSpecies = card => species === 'all' || String(card.dataset.species || '').split(/\s+/).includes(species);
  const matchesTopic = card => topic === 'all' || card.dataset.topic === topic;
  const cardKey = card => card.getAttribute('href') || `${card.dataset.topic}-${card.textContent.trim()}`;
  const getCards = () => [...cardMap.values()];
  const setControlsLoading = (loading) => {
    [...topicButtons, ...speciesButtons].forEach(button => {
      button.disabled = loading;
      button.setAttribute('aria-busy', String(loading));
    });
  };

  function rememberCards(source) {
    [...source.querySelectorAll('[data-guide]')].forEach(card => {
      card.classList.remove('is-featured');
      card.hidden = false;
      cardMap.set(cardKey(card), card);
    });
  }

  function getLastPageFrom(documentRoot) {
    const links = [...documentRoot.querySelectorAll('a[href*="page="]')];
    return links.reduce((max, link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        return Math.max(max, Number(url.searchParams.get('page') || 1));
      } catch (_error) {
        return max;
      }
    }, 1);
  }

  function applyUrlState() {
    const params = new URLSearchParams(window.location.search);
    species = normalizeSpecies(params.get('especie') || 'all');
    topic = normalizeTopic(params.get('tema') || 'all');
    page = Math.max(1, Number(params.get('page') || 1));
  }

  function updateUrl(next = {}, mode = 'push') {
    const params = new URLSearchParams(window.location.search);
    const nextSpecies = normalizeSpecies(next.species ?? species);
    const nextTopic = normalizeTopic(next.topic ?? topic);
    const nextPage = Math.max(1, Number(next.page ?? page));

    if (nextSpecies === 'all') params.delete('especie'); else params.set('especie', nextSpecies);
    if (nextTopic === 'all') params.delete('tema'); else params.set('tema', nextTopic);
    if (nextPage <= 1) params.delete('page'); else params.set('page', String(nextPage));

    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history[mode === 'replace' ? 'replaceState' : 'pushState']({ species: nextSpecies, topic: nextTopic, page: nextPage }, '', url);
    syncTitle(nextPage);
  }

  function syncTitle(nextPage = page) {
    document.title = nextPage > 1 ? `${baseTitle} - Página ${nextPage}` : baseTitle;
  }

  function renderPagination(totalPages) {
    pagination.innerHTML = '';
    if (totalPages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;
    const makeLink = (label, nextPage, disabled = false, current = false) => {
      const link = document.createElement(disabled ? 'span' : 'a');
      link.textContent = label;
      link.className = 'fp-guides-index__page-link';
      if (current) link.setAttribute('aria-current', 'page');
      if (disabled) {
        link.setAttribute('aria-disabled', 'true');
      } else {
        const params = new URLSearchParams(window.location.search);
        if (species === 'all') params.delete('especie'); else params.set('especie', species);
        if (topic === 'all') params.delete('tema'); else params.set('tema', topic);
        if (nextPage <= 1) params.delete('page'); else params.set('page', String(nextPage));
        link.href = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
        link.addEventListener('click', event => {
          event.preventDefault();
          page = nextPage;
          updateUrl({ page });
          render();
          root.querySelector('[data-guide-list]')?.scrollIntoView({ block: 'start' });
        });
      }
      return link;
    };

    pagination.append(makeLink('Anterior', Math.max(1, page - 1), page === 1));
    for (let index = 1; index <= totalPages; index += 1) {
      pagination.append(makeLink(String(index), index, false, index === page));
    }
    pagination.append(makeLink('Siguiente', Math.min(totalPages, page + 1), page === totalPages));
  }

  function render() {
    const cards = getCards();
    const available = cards.filter(matchesSpecies);

    topicButtons.forEach(button => {
      const key = button.dataset.guideTopic;
      const total = available.filter(card => key === 'all' || card.dataset.topic === key).length;
      button.hidden = total === 0;
      button.querySelector('span').textContent = total;
      button.setAttribute('aria-pressed', String(topic === key));
    });

    speciesButtons.forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.guideSpecies === species));
    });

    const visible = available.filter(matchesTopic);
    const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
    const hydratedTotalPages = allPagesLoaded ? totalPages : Math.max(totalPages, initialLastPage, page);
    if (allPagesLoaded && page > totalPages) {
      page = totalPages;
      updateUrl({ page }, 'replace');
    }
    const renderPage = !allPagesLoaded && page > totalPages ? 1 : page;
    const start = (renderPage - 1) * pageSize;
    const pageCards = visible.slice(start, start + pageSize);
    syncTitle(page);

    cards.forEach(card => {
      card.classList.remove('is-featured');
      card.hidden = true;
      grid.append(card);
    });

    if (pageCards[0]) {
      featured.append(pageCards[0]);
      pageCards[0].hidden = false;
      pageCards[0].classList.add('is-featured');
    }

    pageCards.slice(1).forEach(card => {
      card.hidden = false;
      grid.append(card);
    });

    featured.hidden = pageCards.length === 0;
    list.hidden = pageCards.length <= 1;
    if (empty) empty.hidden = visible.length > 0;
    count.textContent = `${visible.length} ${visible.length === 1 ? 'guía' : 'guías'}`;
    renderPagination(hydratedTotalPages);
  }

  async function loadAllPages() {
    if (allPagesLoaded) return allPagesPromise;
    if (loadingAllPages) return allPagesPromise;
    loadingAllPages = true;
    setControlsLoading(true);
    const lastPage = initialLastPage;
    if (lastPage <= 1) {
      allPagesLoaded = true;
      setControlsLoading(false);
      return allPagesPromise;
    }

    const parser = new DOMParser();
    const requests = [];
    const currentPage = Math.max(1, Number(new URLSearchParams(window.location.search).get('page') || 1));
    const currentCards = [...root.querySelectorAll('[data-guide]')].map(card => card.cloneNode(true));
    for (let index = 1; index <= lastPage; index += 1) {
      const url = new URL(window.location.pathname, window.location.origin);
      if (index > 1) url.searchParams.set('page', String(index));
      requests.push(fetch(url.toString(), { credentials: 'same-origin' })
        .then(response => response.ok ? response.text() : '')
        .then(html => ({ index, html }))
        .catch(() => ({ index, html: '' })));
    }
    allPagesPromise = Promise.all(requests).then((pages) => {
      [...root.querySelectorAll('[data-guide]')].forEach(card => card.remove());
      cardMap.clear();
      pages
        .sort((a, b) => a.index - b.index)
        .forEach(({ index, html }) => {
          if (!html && index !== currentPage) return;
          const doc = html ? parser.parseFromString(html, 'text/html') : document.createDocumentFragment();
          if (!html) currentCards.forEach(card => doc.append(card.cloneNode(true)));
          rememberCards(doc);
        });
      allPagesLoaded = true;
      setControlsLoading(false);
      render();
    });
    return allPagesPromise;
  }

  rememberCards(root);
  applyUrlState();
  updateUrl({}, 'replace');

  speciesButtons.forEach(button => button.addEventListener('click', () => {
    species = button.dataset.guideSpecies;
    topic = 'all';
    page = 1;
    updateUrl({ species, topic, page });
    render();
  }));

  topicButtons.forEach(button => button.addEventListener('click', () => {
    topic = button.dataset.guideTopic;
    page = 1;
    updateUrl({ topic, page });
    render();
  }));

  window.addEventListener('popstate', () => {
    applyUrlState();
    render();
  });

  render();
  loadAllPages();
})();
